import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const dpRoot = `Catalogs|Exams|Sections|Modal|`;
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamSectionCatalog,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogExamSectionsModal';
export type SPCatalogsExamSectionsModalLocNames =
	| `${LocNameStart}ErrorMidHighLessThanLow`
	| `${LocNameStart}NotificationUpdateSuccess`
	| `${LocNameStart}NotificationAddSuccess`
	| `${LocNameStart}NotificationAddSuccess`
	| `${LocNameStart}TitleEdit`
	| `${LocNameStart}TitleAdd`
	| `${LocNameStart}LabelExamForm`
	| `${LocNameStart}LabelSectionName`
	| `${LocNameStart}LabelLabel`
	| `${LocNameStart}LabelCoverage`
	| `${LocNameStart}LabelHighScore`
	| `${LocNameStart}LabelMidScore`
	| `${LocNameStart}LabelLowScore`
	| `${LocNameStart}LabelCutScore`
	| `${LocNameStart}LabelMaxScore`
	| `${LocNameStart}ButtonEditSave`
	| `${LocNameStart}ButtonAddSave`
	| `${LocNameStart}ButtonEditCancel`
	| `${LocNameStart}ButtonAddCancel`
	| `${LocNameStart}ErrorExamFormRequired`
	| `${LocNameStart}ErrorSectionNameRequired`
	| `${LocNameStart}ErrorLabelRequired`
	| `${LocNameStart}ErrorHighScoreInvalid`
	| `${LocNameStart}ErrorMidScoreInvalid`
	| `${LocNameStart}ErrorLowScoreInvalid`
	| `${LocNameStart}ErrorCutScoreInvalid`
	| `${LocNameStart}ErrorMaxScoreInvalid`
	| `${LocNameStart}ErrorScoresRequired`
	| `${LocNameStart}ErrorScoresMatch`
	| `${LocNameStart}ErrorHighGreaterThanMid`
	| `${LocNameStart}ErrorCutMaxMatch`
	| `${LocNameStart}ErrorMaxGreaterThanCut`;

export const spCatalogExamSectionModalLocs: LocDefs<SPCatalogsExamSectionsModalLocNames> = {
	SPCatalogExamSectionsModalErrorMidHighLessThanLow: {
		defaultValue: 'Mid score and high score must be greater than or equal to low score',
		description: 'Error when mid score and/or high score are less than low score.',
		displayPath: `${dpRoot}Mid And High Greater Than Low`,
		displayType: 'Error',
		legacyName: 'SPExamSectionsLocMidHighLessThanLow',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalNotificationUpdateSuccess: {
		defaultValue: 'Successfully updated exam section',
		description: 'Update success notification message',
		displayPath: `${dpRoot}Update Success`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.NotificationMessage.EditSuccess',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalNotificationAddSuccess: {
		defaultValue: 'Successfully added exam section',
		description: 'Add success notification message',
		displayPath: `${dpRoot}Add Success`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.NotificationMessage.CreateSuccess',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalTitleEdit: {
		defaultValue: 'Edit Exam Section',
		description: 'Edit title',
		displayPath: `${dpRoot}Edit title`,
		displayType: 'Title',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Title.EditDialog',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalTitleAdd: {
		defaultValue: 'Add Exam Section',
		description: 'Add title',
		displayPath: `${dpRoot}Add title`,
		displayType: 'Title',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Title.CreateDialog',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelExamForm: {
		defaultValue: 'Exam Form',
		description: 'Label for: Exam Form.',
		displayPath: `${dpRoot}Exam Form`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.ExamForm',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelSectionName: {
		defaultValue: 'Section Name',
		description: 'Label for: Section Name.',
		displayPath: `${dpRoot}Section Name`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.SectionName',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelLabel: {
		defaultValue: 'Label',
		description: 'Label for: Label.',
		displayPath: `${dpRoot}Label`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.Label',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelCoverage: {
		defaultValue: 'Coverage',
		description: 'Label for: Coverage.',
		displayPath: `${dpRoot}Coverage`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.Coverage',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelHighScore: {
		defaultValue: 'High',
		description: 'Label for: High.',
		displayPath: `${dpRoot}High`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.HighScore',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelMidScore: {
		defaultValue: 'Mid',
		description: 'Label for: Mid.',
		displayPath: `${dpRoot}Mid`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.MidScore',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelLowScore: {
		defaultValue: 'Low',
		description: 'Label for: Low.',
		displayPath: `${dpRoot}Low`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.LowScore',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelCutScore: {
		defaultValue: 'Cut Score',
		description: 'Label for: Cut Score.',
		displayPath: `${dpRoot}Cut Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.CutScore',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalLabelMaxScore: {
		defaultValue: 'Max Score',
		description: 'Label for: Max Score.',
		displayPath: `${dpRoot}Max Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.MaxScore',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalButtonEditSave: {
		defaultValue: 'Edit Exam Section',
		description: 'Edit save button.',
		displayPath: `${dpRoot}Edit Save`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.EditDialogSave',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalButtonAddSave: {
		defaultValue: 'Add Exam Section',
		description: 'Add save button.',
		displayPath: `${dpRoot}Add Save`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.CreateDialogSave',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalButtonEditCancel: {
		defaultValue: 'Cancel',
		description: 'Edit cancel button.',
		displayPath: `${dpRoot}Edit Cancel`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.EditDialogCancel',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalButtonAddCancel: {
		defaultValue: 'Cancel',
		description: 'Add cancel button.',
		displayPath: `${dpRoot}Add Cancel`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.CreateDialogCancel',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorExamFormRequired: {
		defaultValue: 'Exam form is required',
		description: 'Error: Exam form is required.',
		displayPath: `${dpRoot}Exam form is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ExamFormRequired',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorSectionNameRequired: {
		defaultValue: 'Section name is required',
		description: 'Error: Section name is required.',
		displayPath: `${dpRoot}Section name is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.SectionNameRequired',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorLabelRequired: {
		defaultValue: 'Label is required',
		description: 'Error: Label is required.',
		displayPath: `${dpRoot}Label is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.LabelRequired',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorHighScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error: High score must be a number.',
		displayPath: `${dpRoot}High Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.HighScoreInvalid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorMidScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error: Mid score must be a number.',
		displayPath: `${dpRoot}Mid Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MidInvalid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorLowScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error: Low score must be a number.',
		displayPath: `${dpRoot}Low Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.LowInvalid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorCutScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error: Cut score must be a number.',
		displayPath: `${dpRoot}Cut Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.CutScoreInvalid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorMaxScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error: Max score must be a number.',
		displayPath: `${dpRoot}Max Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MaxScoreInvalid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorScoresRequired: {
		defaultValue: 'High/mid/low scores or cut/max scores must be provided',
		description: 'Error: Scores must be required.',
		displayPath: `${dpRoot}Scores Required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresRequired',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorScoresMatch: {
		defaultValue: 'High score, mid score, and low score must all have values or all be empty',
		description: 'Error: Scores must match.',
		displayPath: `${dpRoot}Scores Match`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresMatch',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorHighGreaterThanMid: {
		defaultValue: 'High score must be greater than or equal to mid score',
		description: 'Error: High score must be greater than or equal to mid score.',
		displayPath: `${dpRoot}High Greater Than Mid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.HighGreaterThanMid',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorCutMaxMatch: {
		defaultValue: 'Cut score and max score must both have values or both be empty',
		description: 'Error: Cut score and max score must both have values or both be empty.',
		displayPath: `${dpRoot}Cut Max Match`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.CutMaxMatch',
		...sharedProperties,
	},
	SPCatalogExamSectionsModalErrorMaxGreaterThanCut: {
		defaultValue: 'Max score must be greater than or equal to cut score',
		description: 'Error: Max score must be greater than or equal to cut score.',
		displayPath: `${dpRoot}Max Greater Than Cut`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MaxGreaterThanCut',
		...sharedProperties,
	},
};

export type SPCatalogsExamSectionsImportLocType = LocMap<SPCatalogsExamSectionsModalLocNames>;
